/**
 * AlshPage — vue ALSH (accueil de loisirs) du jour, staff.
 * Affiche l'effectif et la liste nominative des enfants attendus, avec leur
 * formule (journée / matin / après-midi). Les inscriptions se font via la fiche
 * famille (onglet ALSH).
 */

import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Input, Field, Alert, Badge } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';

interface AlshLigne {
  reservationId: string; enfantId: string; prenom: string; nom: string;
  classe: string | null; ecole: string | null;
  formule: 'journee' | 'matin' | 'apres_midi';
  statut: 'reserve' | 'present' | 'absent' | 'absent_justifie';
}

const FORMULE_LABEL: Record<AlshLigne['formule'], string> = {
  journee: 'Journée', matin: 'Matin', apres_midi: 'Après-midi',
};
const STATUT_LABEL: Record<AlshLigne['statut'], string> = {
  reserve: 'Réservé', present: 'Présent', absent: 'Absent', absent_justifie: 'Absent justifié',
};

export function AlshPage() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [lignes, setLignes] = useState<AlshLigne[]>([]);
  const [effectif, setEffectif] = useState<{ comptes: number; parFormule: { journee: number; matin: number; apresMidi: number } } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false; setLoading(true);
    (async () => {
      try {
        const [liste, eff] = await Promise.all([
          api.scolaire.getListeAlsh(date),
          api.scolaire.getEffectifAlsh(date).catch(() => null),
        ]);
        if (cancelled) return;
        setLignes(liste.lignes);
        setEffectif(eff ? { comptes: eff.comptes, parFormule: eff.parFormule } : null);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiException ? err.message : 'Erreur de chargement');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [date]);

  return (
    <ScolaireLayout activeTab="alsh">
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">ALSH — accueil de loisirs</h1>
          <p className="mt-1 text-sm text-ink-muted">Enfants attendus au centre de loisirs pour la journée (toutes écoles confondues).</p>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="mb-6 max-w-xs">
          <Field label="Date" htmlFor="date-alsh">
            <Input id="date-alsh" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>

        <Card className="mb-6">
          <CardBody>
            <div className="mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
              <h2 className="font-display text-base font-semibold text-ink">Effectif du jour</h2>
            </div>
            <div className="text-4xl font-semibold tabular-nums text-ink">{loading ? '—' : effectif?.comptes ?? 0}</div>
            {effectif && effectif.comptes > 0 && (
              <div className="mt-3 flex gap-4 text-xs text-ink-muted">
                <span>Journée : <strong className="text-ink">{effectif.parFormule.journee}</strong></span>
                <span>Matin : <strong className="text-ink">{effectif.parFormule.matin}</strong></span>
                <span>Après-midi : <strong className="text-ink">{effectif.parFormule.apresMidi}</strong></span>
              </div>
            )}
          </CardBody>
        </Card>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : lignes.length === 0 ? (
          <Card><CardBody><p className="text-sm text-ink-muted">Aucun enfant inscrit à l'ALSH ce jour.</p></CardBody></Card>
        ) : (
          <ul className="space-y-2">
            {lignes.map((l) => (
              <li key={l.reservationId}>
                <Card>
                  <CardBody className="py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-ink">{l.prenom} {l.nom}</span>
                        {l.ecole && <p className="text-xs text-ink-subtle">{l.ecole}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge>{FORMULE_LABEL[l.formule]}</Badge>
                        <span className="text-xs text-ink-muted">{STATUT_LABEL[l.statut]}</span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </main>
    </ScolaireLayout>
  );
}
